// Server-authoritative turn/phase read path (Phase 1, Task 3).
//
// `applyTurnPhaseFromMirror(host, mirrorState)` folds the authoritative
// MatchState the ClientMirror holds (`{ phase, rollOff }`) onto the
// GameBoard fields the renderer already reads: `matchPhase`, `currentTurn`
// / `activePlayerNum`, `turnNumbers`, `currentPhase`, and the dice-modal's
// `firstPlayerRollState`. It is a pure mapping plus a single `host.setState`.
//
// Idempotent: it diffs the mapped fields against `host.state` and only calls
// `setState` with the fields that actually changed. Re-applying the same
// authoritative mirror state is a no-op for the renderer (no setState).
// This retires the rollOff-resurrection / resolveRestoredMatchPhase
// stickiness heuristics for auth matches: the phase comes straight from the
// authoritative mirror, not a local guess.

// Mirror seat ('p1'/'p2') → activePlayerNum (1/2). In multiplayer the local
// user is pinned to one seat, but the active-turn HUD reads activePlayerNum,
// so we derive it from the authoritative turn seat.
function seatToPlayerNum(seat) {
  return seat === 'p2' ? 2 : 1;
}

// rollOff seat strings → modal seat numbers. The modal speaks 1|2; the
// authoritative document speaks 'p1'/'p2'. Convert at this boundary and
// preserve null (no winner / no choice yet) verbatim.
function rollSeatToNum(seat) {
  if (seat === 'p1') return 1;
  if (seat === 'p2') return 2;
  return null;
}

// Compute the modal's firstPlayerRollState shape ({ hostRoll, guestRoll,
// winnerSeat, firstPlayerSeat }, seats as 1|2) from the authoritative mirror's
// rollOff. Exported so the live-feed path can read the NEW value SYNCHRONOUSLY
// The mapper's host.setState is async, so reading host.state right after it
// returns the STALE value (the bug that left the dice un-fed).
export function rollStateFromMirror(mirrorState) {
  const rollOff = mirrorState?.rollOff;
  if (!rollOff) return null;
  return {
    hostRoll: rollOff.p1Roll ?? null,
    guestRoll: rollOff.p2Roll ?? null,
    winnerSeat: rollSeatToNum(rollOff.winnerSeat),
    firstPlayerSeat: rollSeatToNum(rollOff.firstPlayerSeat),
    // Each seat's initiative d20 wears the palette that seat announced.
    hostPalette: mirrorState.players?.p1?.dicePalette ?? null,
    guestPalette: mirrorState.players?.p2?.dicePalette ?? null,
  };
}

export function applyTurnPhaseFromMirror(host, mirrorState) {
  if (!mirrorState) return;
  const phase = mirrorState.phase || {};
  const cur = host.state || {};
  const patch = {};

  if (phase.matchPhase !== undefined && phase.matchPhase !== cur.matchPhase) {
    patch.matchPhase = phase.matchPhase;
  }
  if (phase.currentTurn !== undefined && phase.currentTurn !== cur.currentTurn) {
    patch.currentTurn = phase.currentTurn;
    patch.activePlayerNum = seatToPlayerNum(phase.currentTurn);
  } else if (
    phase.currentTurn !== undefined &&
    cur.activePlayerNum !== seatToPlayerNum(phase.currentTurn) &&
    !soloMulliganOpen(host)
  ) {
    // Coherence heal: a non-fold writer (e.g. the initiative resolver on a
    // reconnect replay, a restored blob) can desync activePlayerNum from
    // currentTurn. The solo pilot's acting seat rides activePlayerNum, so
    // repair on fold. EXCEPT while a solo mulligan is still open: the pilot
    // legitimately dwells on the NON-turn seat to resolve that seat's
    // mulligan (afterMulliganComplete hands the perspective over), and
    // healing mid-window snapped the view back to the turn seat and stranded
    // the other seat's prompt (mulliganDone half-set, start wedged on
    // 'openingDraw').
    patch.activePlayerNum = seatToPlayerNum(phase.currentTurn);
  }
  if (phase.currentPhase !== undefined && phase.currentPhase !== cur.currentPhase) {
    patch.currentPhase = phase.currentPhase;
  }
  if (phase.turnNumbers !== undefined && !sameTurnNumbers(phase.turnNumbers, cur.turnNumbers)) {
    patch.turnNumbers = { ...phase.turnNumbers };
  }
  if (phase.globalTurn !== undefined && phase.globalTurn !== cur.globalTurn) {
    patch.globalTurn = phase.globalTurn;
  }
  // Start-phase draw obligation (valkenhall-qa#158): server state, folded
  // verbatim on a diff AND a Full, so a reconnect or a gap-heal can never
  // lose the prompt. Null once the seat draws (or forfeits at PASS_TURN).
  if (phase.drawPending !== undefined && !sameDrawPending(phase.drawPending, cur.drawPending)) {
    patch.drawPending = phase.drawPending ? { ...phase.drawPending } : null;
  }
  // The draw-prompt latch spans one round trip: the click hides the prompt
  // until this fold. If the obligation cleared, the latch is moot; if the fold
  // still owes the same draw, the DRAW did not land (empty pile, capped,
  // dropped while reconnecting) and the prompt must come back.
  if (cur.drawPromptSent && phase.drawPending !== undefined) {
    patch.drawPromptSent = null;
  }

  const nextRoll = rollStateFromMirror(mirrorState);
  if (nextRoll && !sameRollState(nextRoll, cur.firstPlayerRollState)) {
    patch.firstPlayerRollState = nextRoll;
  }

  // The server says the match is PLAYING. The initiative modal must not be
  // up (a stale blob restore / poisoned autosave can re-arm it over a live
  // board; the normal confirm flow closed it long before 'playing').
  if ((patch.matchPhase ?? cur.matchPhase) === 'playing' && cur.showFirstPlayerDice) {
    patch.showFirstPlayerDice = false;
  }

  if (Object.keys(patch).length === 0) return;
  host.setState(patch);
}

// The solo pilot mulligans BOTH seats sequentially. While either seat's
// mulligan is still open ('prompt'/'selecting'), the perspective pointer may
// legitimately sit on the non-turn seat and must not be healed away.
export function soloMulliganOpen(host) {
  if (!host.props?.isCasualSandbox) return false;
  const open = (p) => p === 'prompt' || p === 'selecting';
  return open(host.state?.mulliganPhase) || open(host.state?.mulliganPhase2);
}

// null and undefined both mean "nothing owed", so a cold-mount undefined
// against a server null is not a change worth a setState.
function sameDrawPending(a, b) {
  if (!a || !b) return !a && !b;
  return a.seat === b.seat && a.globalTurn === b.globalTurn;
}

function sameTurnNumbers(a, b) {
  if (!a || !b) return a === b;
  return (a.p1 ?? 0) === (b.p1 ?? 0) && (a.p2 ?? 0) === (b.p2 ?? 0);
}

function sameRollState(a, b) {
  if (!a || !b) return a === b;
  return a.hostRoll === b.hostRoll
    && a.guestRoll === b.guestRoll
    && a.winnerSeat === b.winnerSeat
    && a.firstPlayerSeat === b.firstPlayerSeat
    && a.hostPalette === b.hostPalette
    && a.guestPalette === b.guestPalette;
}
